import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { BellSimpleIcon as BellSimple, CakeIcon as Cake, HeartIcon as Heart, ChatCircleDotsIcon as ChatCircleDots, BookOpenIcon as BookOpen } from "@phosphor-icons/react";

/**
 * Per-dog notification opt-outs.
 *
 * Surfaces the four lifecycle automations the platform fires on this
 * dog's behalf (birthday, gotcha day, day-30 buyer follow-up,
 * puppy-school drip) and lets the owner mute any subset for THIS
 * dog without touching global notification preferences.
 *
 * Read/write hits the same endpoints used by the cron de-duplication
 * check (`dogs.notification_opt_outs` array). The list is small
 * (~4 items) and toggles save individually with optimistic UI; the
 * server is the source of truth, so a failed toggle reverts.
 */
const KIND_META = {
  birthday: {
    label: "Birthday emails",
    desc: "Once a year, on the dog's birthday — a quick note + Dog Log link.",
    icon: Cake,
  },
  gotcha_day: {
    label: "Gotcha Day milestones",
    desc: "Once a year, on the anniversary of bringing this dog home.",
    icon: Heart,
  },
  buyer_day30: {
    label: "30-day buyer check-in",
    desc: "One-time: 30 days after homecoming, an invite to leave a trust-signal review.",
    icon: ChatCircleDots,
  },
  puppy_school: {
    label: "First-puppy school (7-day drip)",
    desc: "Days 1, 3, 5, 7, 14, 30 — a short lesson at each step.",
    icon: BookOpen,
  },
};

export default function DogNotificationsPanel({ dogId, dogName }) {
  const [state, setState] = useState(null); // { opt_outs: [...], available_kinds: [...] }
  const [savingKind, setSavingKind] = useState(null);

  useEffect(() => {
    if (!dogId) return;
    api.get(`/dogs/${dogId}/notifications`)
      .then((r) => setState(r.data))
      .catch(() => setState({ opt_outs: [], available_kinds: Object.keys(KIND_META) }));
  }, [dogId]);

  const toggle = async (kind) => {
    if (!state) return;
    const isMuted = state.opt_outs.includes(kind);
    const next = isMuted
      ? state.opt_outs.filter((k) => k !== kind)
      : [...state.opt_outs, kind];
    setSavingKind(kind);
    // Optimistic — flip locally, roll back on error.
    setState({ ...state, opt_outs: next });
    try {
      const { data } = await api.patch(`/dogs/${dogId}/notifications`, { opt_outs: next });
      setState((s) => ({ ...(s || {}), opt_outs: data.opt_outs || next }));
      toast.success(isMuted ? "Notifications on." : "Muted.");
    } catch (e) {
      // Revert.
      setState((s) => ({ ...(s || {}), opt_outs: state.opt_outs }));
      toast.error(formatApiError(e));
    } finally {
      setSavingKind(null);
    }
  };

  if (!state) return null;
  const kinds = state.available_kinds && state.available_kinds.length
    ? state.available_kinds
    : Object.keys(KIND_META);

  return (
    <section
      className="mt-10 rounded-2xl border border-[#EAEAEA] bg-white p-5 sm:p-6"
      data-testid="dog-notifications-panel"
    >
      <header className="mb-4 flex items-start gap-3">
        <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-gradient-to-br from-[#FBF7EC] to-[#F3E9C7] border border-[#C9A227]/30 text-[#8A6A00] flex-shrink-0">
          <BellSimple size={18} weight="duotone" />
        </div>
        <div>
          <h3 className="font-spectral text-lg tracking-tight text-ink">
            Notifications for {dogName || "this dog"}
          </h3>
          <p className="text-[12.5px] text-[#6B6B6B] mt-1 leading-relaxed max-w-xl">
            Mute any of the four lifecycle automations for this dog. Won't affect
            other dogs you own. Global email preferences live in{" "}
            <a href="/settings" className="underline hover:text-ink">Settings → Notifications</a>.
          </p>
        </div>
      </header>
      <ul className="space-y-2">
        {kinds.map((kind) => {
          const meta = KIND_META[kind] || { label: kind, desc: "", icon: BellSimple };
          const muted = state.opt_outs.includes(kind);
          const Icon = meta.icon;
          return (
            <li
              key={kind}
              className="flex items-start gap-3 rounded-xl border border-[#F1ECDD] bg-[#FAFAF7] p-3"
              data-testid={`dog-notif-row-${kind}`}
            >
              <Icon size={18} weight="duotone" className="mt-0.5 text-[#8A6A00] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-ink text-[13px]">{meta.label}</p>
                <p className="text-[11.5px] text-[#6B6B6B] mt-0.5 leading-snug">{meta.desc}</p>
              </div>
              <button
                type="button"
                onClick={() => toggle(kind)}
                disabled={savingKind === kind}
                aria-pressed={!muted}
                data-testid={`dog-notif-toggle-${kind}`}
                className={`inline-flex items-center justify-center h-7 px-3 rounded-full text-[10.5px] font-bold uppercase tracking-[0.15em] border transition-colors flex-shrink-0 ${
                  muted
                    ? "bg-white text-[#6B6B6B] border-[#E5E5E5] hover:bg-[#FBF7EC]"
                    : "bg-ink text-white border-ink hover:bg-crimson hover:border-crimson"
                }`}
              >
                {savingKind === kind ? "…" : muted ? "Muted" : "On"}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
